import React from 'react';
import { Component } from 'react';
import { CommercialModel, DashboardRole, InviteCode, PostcodesAccessRule, SpeechNeed, UserType } from '../interfaces';
import { Theme } from '../Theme';
import { Button } from './Button';
import { TableRowEllipsisMenuButton } from './TableRowEllipsisMenuButton';
import '../saas/PlanCatalogAdmin.css';

type Props = {
    authenticatedUserType: UserType;
    dashboardRole: DashboardRole;
    canUseDevelopmentFeatures: boolean;
    canManageInviteCodes: boolean;
    activationKeyError: string | null;
    activationKeyOperationInProgress: boolean;
    inviteCodes: Array<InviteCode>;
    postcodesAccess: Array<PostcodesAccessRule>;
    speechNeeds: Array<SpeechNeed>;
    allSpeechNeeds: Array<SpeechNeed>;
    providerCommercialModel?: CommercialModel;
    providerSpeechNeeds?: Array<string>; // undefined means unrestricted; [] means no support-area access
    onCreateInviteCode: (speechNeedIds: string[], purpose: string) => Promise<boolean>;
    onDeleteInviteCode: (inviteCode: InviteCode) => Promise<boolean>;
    onEditInviteCode: (inviteCode: InviteCode) => Promise<boolean>;
    onCreatePostcodesAccessRule: (postcodesAccessRule: PostcodesAccessRule) => void;
    onEditPostcodesAccessRule: (index: number, postcodesAccessRule: PostcodesAccessRule) => void;
    onDeletePostcodesAccessRule: (index: number) => void;
    onSaveProviderSpeechNeeds: (speechNeedIds: string[]) => void;
};

type State = {
    showDialog: boolean;
    showEditDialog: boolean;
    showPostcodesAccessDialog: boolean;
    showEditPostcodesAccessDialog: boolean;
    selectedSpeechNeedIds: string[];
    purpose: string;
    hoveredInviteCode: string | null;
    editingInviteCode: InviteCode | null;
    editingSpeechNeedIds: string[];
    editingPurpose: string;
    postcodesAccessRule: string;
    postcodesAccessSpeechNeedIds: string[];
    hoveredPostcodesAccessRuleIndex: number | null;
    editingPostcodesAccessRuleIndex: number | null;
    editingPostcodesAccessRule: string;
    editingPostcodesAccessSpeechNeedIds: string[];
    editingProviderSpeechNeedIds: string[] | null; // null mirrors the saved provider access
};

export class SpeechNeedsView extends Component<Props, State> {
    
    constructor(props: Props) {
        super(props);
        this.state = {
            showDialog: false,
            showEditDialog: false,
            showPostcodesAccessDialog: false,
            showEditPostcodesAccessDialog: false,
            selectedSpeechNeedIds: [],
            purpose: '',
            hoveredInviteCode: null,
            editingInviteCode: null,
            editingSpeechNeedIds: [],
            editingPurpose: '',
            postcodesAccessRule: '',
            postcodesAccessSpeechNeedIds: [],
            hoveredPostcodesAccessRuleIndex: null,
            editingPostcodesAccessRuleIndex: null,
            editingPostcodesAccessRule: '',
            editingPostcodesAccessSpeechNeedIds: [],
            editingProviderSpeechNeedIds: null,
        };
    }
    
    private formatDate(date: any): string {
        if (!date) return 'N/A';
        
        // Handle Firestore timestamp or regular date
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        return dateObj.toLocaleDateString();
    }

    private getSpeechNeedName(speechNeedId: string): string {
        const speechNeed = this.props.speechNeeds.find(sn => sn.id === speechNeedId);
        return speechNeed ? speechNeed.name : speechNeedId;
    }

    private getSpeechNeedTypeTextColour(speechNeedType: SpeechNeed['type']): string {
        // Colour smart and stable support areas so their type is visible at a glance.
        if (speechNeedType === 'smart') {
            return 'blue';
        }
        if (speechNeedType === 'stable') {
            return 'orange';
        }
        return Theme.Colours.text;
    }

    private getSpeechNeedTextColour(speechNeedId: string): string {
        const speechNeed = this.props.speechNeeds.find(sn => sn.id === speechNeedId);
        return this.getSpeechNeedTypeTextColour(speechNeed?.type);
    }

    private getOrderedSpeechNeedIds(speechNeedIds: string[], availableSpeechNeeds: Array<SpeechNeed> = this.props.speechNeeds): string[] {
        // Keep selected support areas in the same order as the given support area list.
        const selectedSpeechNeedIds = new Set(speechNeedIds.filter(speechNeedId => speechNeedId));
        const availableSpeechNeedIds = availableSpeechNeeds.map(speechNeed => speechNeed.id);
        const orderedSpeechNeedIds = availableSpeechNeedIds
            .filter(speechNeedId => selectedSpeechNeedIds.has(speechNeedId));
        const unavailableSpeechNeedIds = speechNeedIds
            .filter(speechNeedId => speechNeedId && !availableSpeechNeedIds.includes(speechNeedId));
        return [
            ...orderedSpeechNeedIds,
            ...Array.from(new Set(unavailableSpeechNeedIds)),
        ];
    }

    private getInviteCodeSpeechNeedIds(inviteCode: InviteCode): string[] {
        // Prefer the new multi-support-area field, falling back to the legacy single field.
        const speechNeedIds = inviteCode.speechNeeds && inviteCode.speechNeeds.length > 0
            ? inviteCode.speechNeeds
            : [inviteCode.speechNeed];
        return this.getOrderedSpeechNeedIds(speechNeedIds);
    }

    private renderSpeechNeedNames(speechNeedIds: string[]) {
        // Render the selected support areas as one compact table cell value.
        return speechNeedIds.map((speechNeedId, index) => (
            <React.Fragment key={speechNeedId}>
                {index > 0 && ', '}
                <span style={{ color: this.getSpeechNeedTextColour(speechNeedId) }}>
                    {this.getSpeechNeedName(speechNeedId)}
                </span>
            </React.Fragment>
        ));
    }

    private getSpeechNeedIdsAfterToggle(currentSpeechNeedIds: string[], speechNeedId: string, isSelected: boolean, availableSpeechNeeds: Array<SpeechNeed> = this.props.speechNeeds): string[] {
        // Toggle a support area and re-order the result to match the support area list.
        const selectedSpeechNeedIds = new Set(currentSpeechNeedIds);
        if (isSelected) {
            selectedSpeechNeedIds.add(speechNeedId);
        } else {
            selectedSpeechNeedIds.delete(speechNeedId);
        }
        return this.getOrderedSpeechNeedIds(Array.from(selectedSpeechNeedIds), availableSpeechNeeds);
    }

    private renderSpeechNeedsCheckboxList(
        selectedSpeechNeedIds: string[],
        onToggleSpeechNeed: (speechNeedId: string, isSelected: boolean) => void,
        availableSpeechNeeds: Array<SpeechNeed> = this.props.speechNeeds,
        columns: number = 1
    ) {
        // Render support areas as a multi-select checkbox list.
        const selectedSpeechNeeds = new Set(selectedSpeechNeedIds);
        const availableSpeechNeedIds = new Set(availableSpeechNeeds.map((speechNeed) => speechNeed.id));
        // Keep removed selections visible so an existing key or rule can be repaired after access narrows.
        const unavailableSelectedSpeechNeeds: SpeechNeed[] = selectedSpeechNeedIds
            .filter((speechNeedId) => !availableSpeechNeedIds.has(speechNeedId))
            .map((speechNeedId) => ({ id: speechNeedId, name: `${speechNeedId} (no longer available)` }));
        const displayedSpeechNeeds = [...availableSpeechNeeds, ...unavailableSelectedSpeechNeeds];
        // Multi-column lists fill top-to-bottom first so the reading order is preserved.
        const multiColumnStyle = columns > 1 ? {
            display: 'grid',
            gridAutoFlow: 'column' as const,
            gridTemplateRows: `repeat(${Math.max(1, Math.ceil(displayedSpeechNeeds.length / columns))}, auto)`,
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            columnGap: '24px',
            maxHeight: '300px', // 30% taller than the single-column dialog lists
        } : {};
        return (
            <div style={{ ...styles.speechNeedsCheckboxList, ...multiColumnStyle }}>
                {displayedSpeechNeeds.map(speechNeed => (
                    <label key={speechNeed.id} style={styles.speechNeedCheckboxLabel}>
                        <input
                            type='checkbox'
                            checked={selectedSpeechNeeds.has(speechNeed.id)}
                            onChange={(event) => onToggleSpeechNeed(speechNeed.id, event.currentTarget.checked)}
                        />
                        <span style={{
                            color: availableSpeechNeedIds.has(speechNeed.id) ?
                                this.getSpeechNeedTypeTextColour(speechNeed.type) : '#b22d42'
                        }}>{speechNeed.name}</span>
                    </label>
                ))}
            </div>
        );
    }
    
    private showDialog = () => {
        // Start new activation keys with no support areas selected.
        this.setState({
            showDialog: true,
            selectedSpeechNeedIds: [],
            purpose: '',
        });
    }
    
    private hideDialog = () => {
        this.setState({ 
            showDialog: false,
            selectedSpeechNeedIds: [],
            purpose: '',
        });
    }

    private showEditDialog = (inviteCode: InviteCode) => {
        // Prime the edit form from the selected row before opening the dialog.
        this.setState({
            showEditDialog: true,
            hoveredInviteCode: null,
            editingInviteCode: inviteCode,
            editingSpeechNeedIds: this.getInviteCodeSpeechNeedIds(inviteCode),
            editingPurpose: inviteCode.purpose || '',
        });
    }

    private hideEditDialog = () => {
        // Clear row edit state so a later edit always starts from the chosen row.
        this.setState({
            showEditDialog: false,
            editingInviteCode: null,
            editingSpeechNeedIds: [],
            editingPurpose: '',
        });
    }

    private showPostcodesAccessDialog = () => {
        // Start new postcode access rules with an empty expression and no support areas.
        this.setState({
            showPostcodesAccessDialog: true,
            postcodesAccessRule: '',
            postcodesAccessSpeechNeedIds: [],
        });
    }

    private hidePostcodesAccessDialog = () => {
        // Clear the new-rule form so each creation starts from a blank state.
        this.setState({
            showPostcodesAccessDialog: false,
            postcodesAccessRule: '',
            postcodesAccessSpeechNeedIds: [],
        });
    }

    private showEditPostcodesAccessDialog = (index: number, postcodesAccessRule: PostcodesAccessRule) => {
        // Prime the postcode edit form from the selected table row.
        this.setState({
            showEditPostcodesAccessDialog: true,
            hoveredPostcodesAccessRuleIndex: null,
            editingPostcodesAccessRuleIndex: index,
            editingPostcodesAccessRule: postcodesAccessRule.rule,
            editingPostcodesAccessSpeechNeedIds: this.getOrderedSpeechNeedIds(postcodesAccessRule.speechNeeds || []),
        });
    }

    private hideEditPostcodesAccessDialog = () => {
        // Clear postcode edit state so stale expressions cannot leak into the next edit.
        this.setState({
            showEditPostcodesAccessDialog: false,
            editingPostcodesAccessRuleIndex: null,
            editingPostcodesAccessRule: '',
            editingPostcodesAccessSpeechNeedIds: [],
        });
    }
    
    private handleDelete = async (inviteCode: InviteCode) => {
        // Delete the activation key from the row menu instead of a selected-row toolbar action.
        console.log('Deleting invite code:', inviteCode);
        if (await this.props.onDeleteInviteCode(inviteCode)) {
            this.setState({ hoveredInviteCode: null });
        }
    }
    
    private handleCreate = async () => {
        console.log('Creating invite code:', {
            speechNeedIds: this.state.selectedSpeechNeedIds,
            purpose: this.state.purpose
        });
        if (await this.props.onCreateInviteCode(this.state.selectedSpeechNeedIds, this.state.purpose)) {
            this.hideDialog();
        }
    }

    private handleEditSave = async () => {
        if (!this.state.editingInviteCode) {
            return;
        }

        // Persist only the editable row fields while keeping the activation key stable.
        const speechNeeds = this.state.editingSpeechNeedIds;
        const wasUpdated = await this.props.onEditInviteCode({
            ...this.state.editingInviteCode,
            speechNeed: speechNeeds[0] || '',
            speechNeeds,
            purpose: this.state.editingPurpose,
        });
        if (wasUpdated) {
            this.hideEditDialog();
        }
    }

    private handleSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        this.setState({
            selectedSpeechNeedIds: this.getSpeechNeedIdsAfterToggle(this.state.selectedSpeechNeedIds, speechNeedId, isSelected),
        });
    }

    private handlePurposeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ purpose: e.target.value });
    }

    private handleEditSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        this.setState({
            editingSpeechNeedIds: this.getSpeechNeedIdsAfterToggle(this.state.editingSpeechNeedIds, speechNeedId, isSelected),
        });
    }

    private handleEditPurposeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ editingPurpose: e.target.value });
    }

    private handlePostcodesAccessRuleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Keep the JavaScript expression as plain text until the user saves.
        this.setState({ postcodesAccessRule: e.target.value });
    }

    private handleEditPostcodesAccessRuleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Keep the edited JavaScript expression as plain text until the user saves.
        this.setState({ editingPostcodesAccessRule: e.target.value });
    }

    private handlePostcodesAccessSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        // Toggle assigned support areas for a new postcode access rule.
        this.setState({
            postcodesAccessSpeechNeedIds: this.getSpeechNeedIdsAfterToggle(this.state.postcodesAccessSpeechNeedIds, speechNeedId, isSelected),
        });
    }

    private handleEditPostcodesAccessSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        // Toggle assigned support areas for the postcode access rule being edited.
        this.setState({
            editingPostcodesAccessSpeechNeedIds: this.getSpeechNeedIdsAfterToggle(this.state.editingPostcodesAccessSpeechNeedIds, speechNeedId, isSelected),
        });
    }

    private canSavePostcodesAccessRule(rule: string, speechNeedIds: string[]): boolean {
        // Require both a JavaScript expression and at least one assigned support area.
        return rule.trim().length > 0 && speechNeedIds.length > 0;
    }

    private handleCreatePostcodesAccessRule = () => {
        // Persist a new postcode access rule from the dialog form.
        if (!this.canSavePostcodesAccessRule(this.state.postcodesAccessRule, this.state.postcodesAccessSpeechNeedIds)) {
            return;
        }

        this.props.onCreatePostcodesAccessRule({
            rule: this.state.postcodesAccessRule.trim(),
            speechNeeds: this.state.postcodesAccessSpeechNeedIds,
        });
        this.hidePostcodesAccessDialog();
    }

    private handleEditPostcodesAccessSave = () => {
        // Persist the edited postcode access rule back to its current table index.
        if (
            this.state.editingPostcodesAccessRuleIndex === null ||
            !this.canSavePostcodesAccessRule(this.state.editingPostcodesAccessRule, this.state.editingPostcodesAccessSpeechNeedIds)
        ) {
            return;
        }

        this.props.onEditPostcodesAccessRule(this.state.editingPostcodesAccessRuleIndex, {
            rule: this.state.editingPostcodesAccessRule.trim(),
            speechNeeds: this.state.editingPostcodesAccessSpeechNeedIds,
        });
        this.hideEditPostcodesAccessDialog();
    }

    private handleDeletePostcodesAccessRule = (index: number) => {
        // Delete the postcode access rule from the row menu.
        this.props.onDeletePostcodesAccessRule(index);
        this.setState({ hoveredPostcodesAccessRuleIndex: null });
    }

    private getSavedProviderSpeechNeedIds(): string[] {
        // The optional provider field historically means all support areas when omitted.
        if (this.props.providerSpeechNeeds === undefined) {
            return this.props.allSpeechNeeds.map(speechNeed => speechNeed.id);
        }
        return this.props.providerSpeechNeeds;
    }

    private getDisplayedProviderSpeechNeedIds(): string[] {
        return this.state.editingProviderSpeechNeedIds ?? this.getSavedProviderSpeechNeedIds();
    }

    private isProviderSpeechNeedsDirty(): boolean {
        const editingSpeechNeedIds = this.state.editingProviderSpeechNeedIds;
        if (editingSpeechNeedIds === null) {
            return false;
        }
        const savedSpeechNeedIds = new Set(this.getSavedProviderSpeechNeedIds());
        return editingSpeechNeedIds.length !== savedSpeechNeedIds.size ||
            editingSpeechNeedIds.some(speechNeedId => !savedSpeechNeedIds.has(speechNeedId));
    }

    private handleProviderSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        // Toggle provider access locally; nothing persists until Save.
        this.setState({
            editingProviderSpeechNeedIds: this.getSpeechNeedIdsAfterToggle(
                this.getDisplayedProviderSpeechNeedIds(),
                speechNeedId,
                isSelected,
                this.props.allSpeechNeeds
            ),
        });
    }

    private handleSaveProviderSpeechNeeds = () => {
        // Save only an explicitly edited selection; omission continues to mean unrestricted.
        this.props.onSaveProviderSpeechNeeds(this.getDisplayedProviderSpeechNeedIds());
        this.setState({ editingProviderSpeechNeedIds: null });
    }

    private handleCancelProviderSpeechNeeds = () => {
        this.setState({ editingProviderSpeechNeedIds: null });
    }

    private renderProviderSpeechNeedsSection() {
        // Render the super-user editor for which support areas this provider can use.
        if (this.props.providerCommercialModel === 'self_service') {
            return (
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <div style={styles.restrictedSectionHeading}>
                            <span className='catalog-admin__root-badge'>Root &amp; Super</span>
                            <h2 style={styles.sectionTitle}>Support areas access</h2>
                        </div>
                    </div>
                    <p style={styles.sectionHint}>
                        Managed by this organisation&apos;s tier in Tools → SaaS plan catalogue (Root only).
                        Changes there are applied to every self-service subscriber on the tier.
                    </p>
                </div>
            );
        }
        const isDirty = this.isProviderSpeechNeedsDirty();
        const isUnconfigured = this.props.providerSpeechNeeds === undefined;
        const canSave = this.props.allSpeechNeeds.length > 0 && isDirty;

        return (
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <div style={styles.restrictedSectionHeading}>
                        <span className='catalog-admin__root-badge'>Root &amp; Super</span>
                        <h2 style={styles.sectionTitle}>Support areas access</h2>
                    </div>
                    {canSave && (
                        <div style={styles.sectionHeaderButtons}>
                            {isDirty && (
                                <Button onClick={this.handleCancelProviderSpeechNeeds} label='Cancel' type='secondary' />
                            )}
                            <Button onClick={this.handleSaveProviderSpeechNeeds} label='Save' type='primary' />
                        </div>
                    )}
                </div>
                <p style={styles.sectionHint}>
                    Support areas this organisation has access to.
                    {isUnconfigured && ' No access list is saved, so every support area is available.'}
                </p>
                {this.props.allSpeechNeeds.length === 0 ? (
                    <p style={styles.sectionHint}>No support areas available.</p>
                ) : (
                    this.renderSpeechNeedsCheckboxList(
                        this.getDisplayedProviderSpeechNeedIds(),
                        this.handleProviderSpeechNeedToggle,
                        this.props.allSpeechNeeds,
                        2
                    )
                )}
            </div>
        );
    }

    private renderPostcodesAccessSection() {
        // Render Root and Super postcode access rules before the activation key table.
        const { postcodesAccess } = this.props;

        return (
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <div style={styles.restrictedSectionHeading}>
                        <span className='catalog-admin__root-badge'>Root &amp; Super</span>
                        <h2 style={styles.sectionTitle}>Postcodes access</h2>
                    </div>
                    <Button onClick={this.showPostcodesAccessDialog} label='New' icon={require('./assets/icons/add_selected.svg').default} type='primary' />
                </div>

                <table style={styles.table}>
                    <thead>
                        <tr style={styles.headerRow}>
                            <th style={styles.headerCell}>rule</th>
                            <th style={styles.headerCell}>speechNeeds</th>
                            <th style={styles.actionsHeaderCell}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {postcodesAccess.length === 0 ? (
                            <tr>
                                <td style={styles.emptyTableCell} colSpan={3}>No postcode access rules.</td>
                            </tr>
                        ) : (
                            postcodesAccess.map((postcodesAccessRule, idx) => {
                                const isHovered = this.state.hoveredPostcodesAccessRuleIndex === idx;
                                return (
                                    <tr
                                        key={`${postcodesAccessRule.rule}-${idx}`}
                                        style={{ backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent' }}
                                        onMouseEnter={() => this.setState({ hoveredPostcodesAccessRuleIndex: idx })}
                                        onMouseLeave={() => {
                                            if (this.state.hoveredPostcodesAccessRuleIndex === idx) {
                                                this.setState({ hoveredPostcodesAccessRuleIndex: null });
                                            }
                                        }}
                                    >
                                        <td style={styles.tableCell}>
                                            <code style={styles.ruleCode}>{postcodesAccessRule.rule}</code>
                                        </td>
                                        <td style={styles.tableCell}>
                                            {this.renderSpeechNeedNames(this.getOrderedSpeechNeedIds(postcodesAccessRule.speechNeeds || []))}
                                        </td>
                                        <td style={styles.actionsCell}>
                                            <TableRowEllipsisMenuButton
                                                ariaLabel={`Options for postcode access rule ${idx + 1}`}
                                                isVisible={isHovered}
                                                options={[{
                                                    label: 'Edit',
                                                    onClick: () => this.showEditPostcodesAccessDialog(idx, postcodesAccessRule),
                                                }, {
                                                    label: 'Delete',
                                                    destructive: true,
                                                    onClick: () => this.handleDeletePostcodesAccessRule(idx),
                                                }]}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { inviteCodes } = this.props;
        const canManageInviteCodes = this.props.canManageInviteCodes;

        return (
            <div style={styles.container}>
                <div style={styles.tableContainer}>

                    {this.props.canUseDevelopmentFeatures && this.renderProviderSpeechNeedsSection()}

                    {this.props.canUseDevelopmentFeatures && this.renderPostcodesAccessSection()}

                    <div style={styles.section} aria-busy={this.props.activationKeyOperationInProgress}>
                        <div style={styles.sectionHeader}>
                            <h2 style={styles.sectionTitle}>Activation keys</h2>
                            {canManageInviteCodes && (
                                <Button
                                    onClick={this.showDialog}
                                    label='New'
                                    icon={require('./assets/icons/add_selected.svg').default}
                                    type='primary'
                                    disabled={this.props.activationKeyOperationInProgress}
                                />
                            )}
                        </div>

                        {this.props.activationKeyError && (
                            <div role='alert' style={styles.errorBanner}>
                                {this.props.activationKeyError}
                            </div>
                        )}

                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.headerRow}>
                                    <th style={styles.headerCell}>Support areas</th>
                                    <th style={styles.headerCell}>Activation key</th>
                                    <th style={styles.headerCell}>Created</th>
                                    <th style={styles.headerCell}>Purpose</th>
                                    {canManageInviteCodes && (
                                        <th style={styles.actionsHeaderCell}></th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {inviteCodes.map((inviteCode, idx) => {
                                    const isHovered = this.state.hoveredInviteCode === inviteCode.code;
                                    return (
                                        <tr
                                            key={inviteCode.code || idx}
                                            style={{ backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent' }}
                                            onMouseEnter={() => this.setState({ hoveredInviteCode: inviteCode.code })}
                                            onMouseLeave={() => {
                                                if (this.state.hoveredInviteCode === inviteCode.code) {
                                                    this.setState({ hoveredInviteCode: null });
                                                }
                                            }}
                                        >
                                            <td style={styles.tableCell}>{this.renderSpeechNeedNames(this.getInviteCodeSpeechNeedIds(inviteCode))}</td>
                                            <td style={styles.tableCell}>{inviteCode.code}</td>
                                            <td style={styles.tableCell}>{this.formatDate(inviteCode.createdAt)}</td>
                                            <td style={styles.tableCell}>{inviteCode.purpose}</td>
                                            {canManageInviteCodes && (
                                                <td style={styles.actionsCell}>
                                                    <TableRowEllipsisMenuButton
                                                        ariaLabel={`Options for activation key ${inviteCode.code}`}
                                                        isVisible={isHovered}
                                                        options={[{
                                                            label: 'Edit',
                                                            disabled: this.props.activationKeyOperationInProgress,
                                                            onClick: () => this.showEditDialog(inviteCode),
                                                        }, {
                                                            label: 'Delete',
                                                            destructive: true,
                                                            disabled: this.props.activationKeyOperationInProgress,
                                                            onClick: () => this.handleDelete(inviteCode),
                                                        }]}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Postcodes Access Dialog */}
                {this.props.canUseDevelopmentFeatures && this.state.showPostcodesAccessDialog && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>Create postcode access rule</h3>

                            <div style={styles.formField}>
                                <label style={styles.label}>Rule:</label>
                                <textarea
                                    value={this.state.postcodesAccessRule}
                                    onChange={this.handlePostcodesAccessRuleChange}
                                    placeholder='data.codes.admin_district == "E06000061"'
                                    style={styles.textarea}
                                />
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Support areas:</label>
                                {this.renderSpeechNeedsCheckboxList(this.state.postcodesAccessSpeechNeedIds, this.handlePostcodesAccessSpeechNeedToggle)}
                            </div>

                            <div style={styles.dialogButtons}>
                                <Button onClick={this.hidePostcodesAccessDialog} label='Cancel' type='secondary' />
                                <Button
                                    onClick={this.handleCreatePostcodesAccessRule}
                                    label='Create'
                                    type='primary'
                                    disabled={!this.canSavePostcodesAccessRule(this.state.postcodesAccessRule, this.state.postcodesAccessSpeechNeedIds)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Postcodes Access Dialog */}
                {this.props.canUseDevelopmentFeatures && this.state.showEditPostcodesAccessDialog &&
                    this.state.editingPostcodesAccessRuleIndex !== null && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>Edit postcode access rule</h3>

                            <div style={styles.formField}>
                                <label style={styles.label}>Rule:</label>
                                <textarea
                                    value={this.state.editingPostcodesAccessRule}
                                    onChange={this.handleEditPostcodesAccessRuleChange}
                                    placeholder='data.codes.admin_district == "E06000061"'
                                    style={styles.textarea}
                                />
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Support areas:</label>
                                {this.renderSpeechNeedsCheckboxList(this.state.editingPostcodesAccessSpeechNeedIds, this.handleEditPostcodesAccessSpeechNeedToggle)}
                            </div>

                            <div style={styles.dialogButtons}>
                                <Button onClick={this.hideEditPostcodesAccessDialog} label='Cancel' type='secondary' />
                                <Button
                                    onClick={this.handleEditPostcodesAccessSave}
                                    label='Save'
                                    type='primary'
                                    disabled={!this.canSavePostcodesAccessRule(this.state.editingPostcodesAccessRule, this.state.editingPostcodesAccessSpeechNeedIds)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Dialog */}
                {canManageInviteCodes && this.state.showDialog && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>Create new activation key</h3>

                            <div style={styles.formField}>
                                <label style={styles.label}>Support areas:</label>
                                {this.renderSpeechNeedsCheckboxList(this.state.selectedSpeechNeedIds, this.handleSpeechNeedToggle)}
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Purpose (optional):</label>
                                <input
                                    type='text'
                                    value={this.state.purpose}
                                    onChange={this.handlePurposeChange}
                                    placeholder='Enter purpose...'
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.dialogButtons}>
                                <Button
                                    onClick={this.hideDialog}
                                    label='Cancel'
                                    type='secondary'
                                    disabled={this.props.activationKeyOperationInProgress}
                                />
                                <Button
                                    onClick={this.handleCreate}
                                    label={this.props.activationKeyOperationInProgress ? 'Creating…' : 'Create'}
                                    type='primary'
                                    disabled={
                                        this.props.activationKeyOperationInProgress ||
                                        this.state.selectedSpeechNeedIds.length === 0
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Dialog */}
                {canManageInviteCodes && this.state.showEditDialog && this.state.editingInviteCode && (
                    <div style={styles.dialogOverlay}>
                        <div style={styles.dialog}>
                            <h3 style={styles.dialogTitle}>Edit activation key</h3>

                            <div style={styles.formField}>
                                <label style={styles.label}>Support areas:</label>
                                {this.renderSpeechNeedsCheckboxList(this.state.editingSpeechNeedIds, this.handleEditSpeechNeedToggle)}
                            </div>

                            <div style={styles.formField}>
                                <label style={styles.label}>Purpose (optional):</label>
                                <input
                                    type='text'
                                    value={this.state.editingPurpose}
                                    onChange={this.handleEditPurposeChange}
                                    placeholder='Enter purpose...'
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.dialogButtons}>
                                <Button
                                    onClick={this.hideEditDialog}
                                    label='Cancel'
                                    type='secondary'
                                    disabled={this.props.activationKeyOperationInProgress}
                                />
                                <Button
                                    onClick={this.handleEditSave}
                                    label={this.props.activationKeyOperationInProgress ? 'Saving…' : 'Save'}
                                    type='primary'
                                    disabled={
                                        this.props.activationKeyOperationInProgress ||
                                        this.state.editingSpeechNeedIds.length === 0
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const styles = {
    container: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        flexGrow: 1,
    },
    section: {
        marginBottom: '32px',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px',
    },
    sectionTitle: {
        ...Theme.Fonts.h2,
        margin: 0,
        color: Theme.Colours.text,
    },
    // Stack the access label above restricted section titles with consistent spacing.
    restrictedSectionHeading: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        gap: '8px',
    },
    sectionHeaderButtons: {
        display: 'flex',
        gap: '12px',
    },
    sectionHint: {
        ...Theme.Fonts.body,
        margin: '0 0 16px 0',
        color: Theme.Colours.text,
        opacity: 0.7,
    },
    errorBanner: {
        ...Theme.Fonts.body,
        margin: '0 0 16px 0',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #d32f2f',
        backgroundColor: 'rgba(211, 47, 47, 0.08)',
        color: '#9f1f1f',
    },
    tableContainer: { 
        flexGrow: 1, 
        overflowY: 'auto' as const, 
        paddingBottom: '20px',
    },
    table: {
        width: '100%',
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
    },
    headerRow: {
        backgroundColor: Theme.Colours.tableHeader,
        color: Theme.Colours.text,
    },
    headerCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px 16px 24px', // top right bottom left
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
    },
    actionsHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'right' as const,
        borderBottom: Theme.Colours.border,
        width: '82px',
    },
    tableCell: {
        ...Theme.Fonts.body,
        padding: '16px 24px 16px 24px', // top right bottom left
        borderBottom: Theme.Colours.border,
    },
    emptyTableCell: {
        ...Theme.Fonts.body,
        padding: '20px 24px',
        borderBottom: Theme.Colours.border,
        color: Theme.Colours.text,
    },
    ruleCode: {
        ...Theme.Fonts.body,
        fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        whiteSpace: 'pre-wrap' as const,
        wordBreak: 'break-word' as const,
        color: Theme.Colours.text,
    },
    actionsCell: {
        ...Theme.Fonts.body,
        padding: '10px 24px',
        borderBottom: Theme.Colours.border,
        textAlign: 'right' as const,
        width: '82px',
    },
    dialogOverlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    dialog: {
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '12px',
        padding: '32px',
        minWidth: '400px',
        maxWidth: '500px',
        border: Theme.Colours.border,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    dialogTitle: {
        ...Theme.Fonts.h2,
        margin: '0 0 24px 0',
        color: Theme.Colours.text,
    },
    formField: {
        marginBottom: '20px',
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        display: 'block',
        marginBottom: '8px',
        color: Theme.Colours.text,
    },
    speechNeedsCheckboxList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '10px',
        maxHeight: '220px',
        overflowY: 'auto' as const,
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        boxSizing: 'border-box' as const,
    },
    speechNeedCheckboxLabel: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: Theme.Colours.text,
        cursor: 'pointer',
    },
    select: {
        ...Theme.Fonts.body,
        width: '100%',
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
    },
    input: {
        ...Theme.Fonts.body,
        width: '100%',
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    textarea: {
        ...Theme.Fonts.body,
        width: '100%',
        minHeight: '96px',
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
        resize: 'vertical' as const,
        fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    },
    dialogButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px',
    },
}
